import { downloadCsvText } from '@/modules/DataAnalysis/Risk/riskCsv';

// "Minimum Temperature (°F)" -> "minimum_temperature_(f)": the column names the
// chart exports have always used.
export function csvColumnName(label) {
    return label
        .replaceAll(' ', '_')
        .replaceAll(',', '')
        .replaceAll('°', '')
        .toLowerCase();
}

// Builds the CSV of a Plotly chart's traces: one row per real date (ascending) and
// one column per series. Comparison years are stacked in sequence under the same
// column instead of side by side, so the file loads straight into a spreadsheet or
// a dataframe.
//
// A trace may carry export-only fields next to the Plotly ones:
//   csvX       real dates, when `x` holds the dates a compared year is moved to so
//              it overlaps the selected period (see compareAxis.js)
//   csvName    column name, when `name` carries a "(2024)" year suffix
//   csvOnAxis  a series that belongs to no year (Long Term Average), plotted on the
//              axis itself: it is repeated on every row sitting at the same axis
//              position, i.e. the same day of each compared season
export function buildChartCsv(traces) {
    if (!traces || traces.length == 0 || traces.every(trace => trace.x.length == 0)) return '';

    const columnName = trace => csvColumnName(trace.csvName || trace.name);
    const columns = [...new Set(traces.map(columnName))];

    const rowsByDate = new Map();
    const rowFor = (date) => {
        if (!rowsByDate.has(date)) rowsByDate.set(date, { date });
        return rowsByDate.get(date);
    };

    // Axis position of each real date, from the traces that moved theirs
    const axisDate = new Map();

    traces.filter(trace => !trace.csvOnAxis).forEach(trace => {
        const column = columnName(trace);
        (trace.csvX || trace.x).forEach((date, i) => {
            rowFor(date)[column] = trace.y[i];
            if (trace.csvX) axisDate.set(date, trace.x[i]);
        });
    });

    traces.filter(trace => trace.csvOnAxis).forEach(trace => {
        const column = columnName(trace);
        // Nothing else to attach to (no year loaded yet): export it on its own dates
        if (rowsByDate.size == 0) trace.x.forEach(rowFor);

        const byAxisDate = new Map(trace.x.map((date, i) => [date, trace.y[i]]));
        rowsByDate.forEach(row => {
            const position = axisDate.has(row.date) ? axisDate.get(row.date) : row.date;
            if (byAxisDate.has(position)) row[column] = byAxisDate.get(position);
        });
    });

    const rows = [...rowsByDate.values()]
        .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
        .map(row => [row.date, ...columns.map(column => (row[column] != null ? row[column] : ''))].join(','));

    return [['date', ...columns].join(','), ...rows].join('\n');
}

// File name of an annual "Historical data" chart, after its card title:
// "annual_precipitation(in)", "annual_average_minimum_temperature(F)".
export function annualChartCsvFilename(id, variable) {
    const name = variable.description.replace(/\s*\(.*\)$/, '');
    const title = id == 'totR' ? 'Annual Precipitation' : `Annual Average ${name}`;
    return `${csvColumnName(title)}(${variable.unit.replace('°', '')})`;
}

// Returns false when the chart has nothing to export.
export function downloadChartCsv(traces, filename) {
    const csv = buildChartCsv(traces);
    if (!csv) return false;
    downloadCsvText(csv, `${filename}.csv`);
    return true;
}
